import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, VideoCamera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { AdminVideoCard } from "@/components/admin/videos/AdminVideoCard";
import { videosQueryOptions } from "@/api-queries/videos";
import { usePagination } from "@/hooks/usePagination";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/dashboard/admin/videos/")({
  component: AdminVideos,
});

type StatusFilter = "all" | "published" | "draft";
const PAGE_SIZE = 12;

function AdminVideos() {
  const queryClient = useQueryClient();
  const { page, setPage, searchQuery, debouncedSearch, setSearch, filters, setFilter } =
    usePagination<{ status: StatusFilter }>({ pageSize: PAGE_SIZE }, { status: "all" });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery(
    videosQueryOptions({ status: filters.status, q: debouncedSearch || undefined, page, limit: PAGE_SIZE }),
  );

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/videos/by-id/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, "Brisanje nije uspjelo");
    },
    onSuccess: () => {
      toast.success("Video obrisan");
      setDeletingId(null);
      queryClient.invalidateQueries({ queryKey: ["videos"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const videos = data?.data ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl" style={{ fontFamily: "var(--font-heading)" }}>
            Video
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Video sadržaj na javnoj stranici /video. Upload fajla ili YouTube i Vimeo link, filtriranje po tagovima.
          </p>
        </div>
        <Button asChild>
          <Link to="/dashboard/admin/videos/new">
            <Plus className="size-4" />
            Novi video
          </Link>
        </Button>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SegmentedFilter<StatusFilter>
          options={[
            { value: "all", label: "Svi" },
            { value: "published", label: "Objavljeni" },
            { value: "draft", label: "Nacrti" },
          ]}
          value={filters.status}
          onChange={(v) => setFilter("status", v)}
          ariaLabel="Status"
        />
        <Input value={searchQuery} onChange={(e) => setSearch(e.target.value)} placeholder="Pretraži…" className="sm:max-w-xs" />
      </div>

      {isLoading ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-2xl border border-border bg-card">
              <div className="aspect-video bg-muted" />
              <div className="space-y-2 p-4">
                <div className="h-3 w-20 rounded bg-muted" />
                <div className="h-5 w-full rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : videos.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border py-16 text-center">
          <VideoCamera className="size-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">{debouncedSearch ? "Nema videa za ovu pretragu." : "Još nema videa. Dodajte prvi."}</p>
        </div>
      ) : (
        <>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((v) => (
              <AdminVideoCard key={v.id} video={v} onDelete={() => setDeletingId(v.id)} />
            ))}
          </div>
          {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
        </>
      )}

      <ConfirmDialog
        open={deletingId !== null}
        title="Obriši video"
        description="Video i njegovi tagovi nestaju s javne stranice. Uploadovani fajl ostaje u media biblioteci."
        confirmLabel="Obriši"
        tone="destructive"
        isPending={deleteMutation.isPending}
        onClose={() => setDeletingId(null)}
        onConfirm={() => deletingId && deleteMutation.mutate(deletingId)}
      />
    </div>
  );
}
